import React, { FC, useState } from 'react';
import { graphql } from 'gatsby';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import {
  StyledSection,
  StyledContentBlock,
  MainContainer,
  MainWrapper,
  FooterGrid,
  FooterColumn,
  ColumnTitle,
  StoreWrapper,
  GooglePlay,
  GooglePlayImageWrapper,
  AppStore,
  AppStoreImageWrapper,
  LinkList,
  LinkItem,
  StyledLink,
  DownWrapper,
  LogoContentWrapper,
  LogoImage,
  Copyright,
  CopyrightText,
  MobileWrapper,
  MobileDownloadSection,
  MobileLinkWrapper,
  MobileSectionSeparator,
  MobileSectionHeader,
  MobileSectionTitle,
  ToggleIcon,
  MobileSectionContent,
  MobileLinkList,
  MobileLinkItem,
  MobileLink,
  MobileFooterBottom,
  MobileLogoWrapper,
  MobileCopyright,
} from './styled';

interface FooterLink {
  message: string;
  href?: string;
}

interface FooterSection {
  message: string;
  id: string;
  links: FooterLink[];
}

interface Props {
  footer?: {
    logo?: {
      src?: string;
      alt?: string;
    };
    links?: FooterSection[];
    appstore?: {
      src?: string;
      alt?: string;
      link?: string;
    };
    playstore?: {
      src?: string;
      alt?: string;
      link?: string;
    };
    copyright: string;
  };
}

const Footer: FC<Props> = ({ footer }) => {
  const [expandedSection, setExpandedSection] = useState('');

  // Function to toggle mobile section expansion
  const toggleSection = (sectionId: string) => {
    if (expandedSection === sectionId) {
      setExpandedSection('');
    } else {
      setExpandedSection(sectionId);
    }
  };

  // Updated function to find links by ID instead of title
  const findLinksById = (id: string): FooterLink[] => {
    const found = footer?.links?.find((item: FooterSection) => item?.id === id);
    return found ? found.links : [];
  };

  // Updated function to find section title by ID
  const findTitleById = (id: string): string => {
    const found = footer?.links?.find((item: FooterSection) => item?.id === id);
    return found ? found.message : '';
  };

  // Define mobile sections using IDs - these should match the IDs in your JSON
  const mobileSections = [
    { id: 'company' },
    { id: 'insights' },
    { id: 'products' },
    { id: 'reviews' },
    { id: 'follow' },
  ];

  return (
    <StyledSection>
      <StyledContentBlock>
        {footer && (
          <MainContainer>
            <MainWrapper>
              <FooterGrid>
                <FooterColumn>
                  <ColumnTitle>Download App</ColumnTitle>
                  <StoreWrapper>
                    <GooglePlay>
                      {/* eslint-disable-next-line react/jsx-no-target-blank */}
                      <a href={footer?.playstore?.link || undefined} target="_blank">
                        <GooglePlayImageWrapper>
                          <LazyLoadImage
                            src={footer?.playstore?.src || undefined}
                            alt={footer?.playstore?.alt || undefined}
                          />
                        </GooglePlayImageWrapper>
                      </a>
                    </GooglePlay>
                    <AppStore>
                      {/* eslint-disable-next-line react/jsx-no-target-blank */}
                      <a href={footer?.appstore?.link || undefined} target="_blank">
                        <AppStoreImageWrapper>
                          <LazyLoadImage
                            src={footer?.appstore?.src || undefined}
                            alt={footer?.appstore?.alt || undefined}
                          />
                        </AppStoreImageWrapper>
                      </a>
                    </AppStore>
                  </StoreWrapper>
                </FooterColumn>
                <FooterColumn>
                  <ColumnTitle>{findTitleById('company')}</ColumnTitle>
                  <LinkList>
                    {findLinksById('company')?.map((link, linkIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <LinkItem key={`company-${linkIndex}`}>
                        <StyledLink href={link?.href || undefined} target="_blank">
                          {link?.message}
                        </StyledLink>
                      </LinkItem>
                    ))}
                  </LinkList>
                </FooterColumn>
                <FooterColumn>
                  <ColumnTitle>{findTitleById('insights')}</ColumnTitle>
                  <LinkList>
                    {findLinksById('insights')?.map((link, linkIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <LinkItem key={`insights-${linkIndex}`}>
                        <StyledLink href={link?.href || undefined} target="_blank">
                          {link?.message}
                        </StyledLink>
                      </LinkItem>
                    ))}
                  </LinkList>
                </FooterColumn>
                <FooterColumn>
                  <ColumnTitle>{findTitleById('products')}</ColumnTitle>
                  <LinkList>
                    {findLinksById('products')?.map((link, linkIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <LinkItem key={`products-${linkIndex}`}>
                        <StyledLink href={link?.href || undefined} target="_blank">
                          {link?.message}
                        </StyledLink>
                      </LinkItem>
                    ))}
                  </LinkList>
                </FooterColumn>
                <FooterColumn>
                  <ColumnTitle>{findTitleById('reviews')}</ColumnTitle>
                  <LinkList>
                    {findLinksById('reviews')?.map((link, linkIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <LinkItem key={`reviews-${linkIndex}`}>
                        <StyledLink href={link?.href || undefined} target="_blank">
                          {link?.message}
                        </StyledLink>
                      </LinkItem>
                    ))}
                  </LinkList>
                </FooterColumn>
                <FooterColumn>
                  <ColumnTitle>{findTitleById('follow')}</ColumnTitle>
                  <LinkList>
                    {findLinksById('follow')?.map((link, linkIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <LinkItem key={`follow-${linkIndex}`}>
                        <StyledLink href={link?.href || undefined} target="_blank">
                          {link?.message}
                        </StyledLink>
                      </LinkItem>
                    ))}
                  </LinkList>
                </FooterColumn>
              </FooterGrid>
              <DownWrapper>
                <LogoContentWrapper>
                  <LogoImage
                    src={footer?.logo?.src || undefined}
                    alt={footer?.logo?.alt || undefined}
                  />
                </LogoContentWrapper>
                <Copyright>
                  <CopyrightText>{footer.copyright}</CopyrightText>
                </Copyright>
              </DownWrapper>
            </MainWrapper>
            <MobileWrapper>
              <MobileDownloadSection>
                <ColumnTitle>Download App</ColumnTitle>
                <StoreWrapper>
                  <GooglePlay>
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a href={footer?.playstore?.link || undefined} target="_blank" aria-label="Download on Google Play">
                      <GooglePlayImageWrapper>
                        <LazyLoadImage
                          src={footer?.playstore?.src || undefined}
                          alt={footer?.playstore?.alt || undefined}
                        />
                      </GooglePlayImageWrapper>
                    </a>
                  </GooglePlay>
                  <AppStore>
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a href={footer?.appstore?.link || undefined} target="_blank" aria-label="Download on App Store">
                      <AppStoreImageWrapper>
                        <LazyLoadImage
                          src={footer?.appstore?.src || undefined}
                          alt={footer?.appstore?.alt || undefined}
                        />
                      </AppStoreImageWrapper>
                    </a>
                  </AppStore>
                </StoreWrapper>
              </MobileDownloadSection>
              <MobileLinkWrapper>
                <MobileSectionSeparator />
                {mobileSections.map(section => (
                  <React.Fragment key={section.id}>
                    <MobileSectionHeader
                      onClick={() => toggleSection(section.id)}
                    >
                      <MobileSectionTitle>
                        {findTitleById(section.id)}
                      </MobileSectionTitle>
                      <ToggleIcon>
                        {expandedSection === section.id ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="31"
                            height="31"
                            viewBox="0 0 31 31"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_6344_99941)">
                              <path
                                d="M15.6875 30.3477C23.9718 30.3477 30.6875 23.6319 30.6875 15.3477C30.6875 7.06338 23.9718 0.347656 15.6875 0.347656C7.40322 0.347656 0.6875 7.06338 0.6875 15.3477C0.6875 23.6319 7.40322 30.3477 15.6875 30.3477Z"
                                fill="#FF9500"
                              />
                              <path
                                d="M21 16.6049V15.0879H10V16.6049H21Z"
                                fill="black"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_6344_99941">
                                <rect
                                  width="31"
                                  height="30"
                                  fill="white"
                                  transform="translate(0 0.347656)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="31"
                            height="31"
                            viewBox="0 0 31 31"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_6344_99941)">
                              <path
                                d="M15.6875 30.3477C23.9718 30.3477 30.6875 23.6319 30.6875 15.3477C30.6875 7.06338 23.9718 0.347656 15.6875 0.347656C7.40322 0.347656 0.6875 7.06338 0.6875 15.3477C0.6875 23.6319 7.40322 30.3477 15.6875 30.3477Z"
                                fill="#FF9500"
                              />
                              <path
                                d="M16.2592 10.3477H14.7422V21.3477H16.2592V10.3477Z"
                                fill="black"
                              />
                              <path
                                d="M21 16.6049V15.0879H10V16.6049H21Z"
                                fill="black"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_6344_99941">
                                <rect
                                  width="31"
                                  height="30"
                                  fill="white"
                                  transform="translate(0 0.347656)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        )}
                      </ToggleIcon>
                    </MobileSectionHeader>
                    <MobileSectionContent
                      isExpanded={expandedSection === section.id}
                    >
                      <MobileLinkList>
                        {findLinksById(section.id)?.map((link: FooterLink, linkIndex: number) => (
                          <MobileLinkItem
                            // eslint-disable-next-line react/no-array-index-key
                            key={`mobile-${section.id}-${linkIndex}`}
                          >
                            <MobileLink href={link?.href || undefined} target="_blank">
                              {link?.message}
                            </MobileLink>
                          </MobileLinkItem>
                        ))}
                      </MobileLinkList>
                    </MobileSectionContent>
                  </React.Fragment>
                ))}
              </MobileLinkWrapper>
              <MobileFooterBottom>
                <MobileLogoWrapper>
                  <LogoImage
                    src={footer?.logo?.src || undefined}
                    alt={footer?.logo?.alt || undefined}
                  />
                </MobileLogoWrapper>
                <MobileCopyright>{footer.copyright}</MobileCopyright>
              </MobileFooterBottom>
            </MobileWrapper>
          </MainContainer>
        )}
      </StyledContentBlock>
    </StyledSection>
  );
};

export const query = graphql`
  fragment SeoFooter on Json {
    footer {
      logo {
        src
        alt
      }
      links {
        message
        id
        links {
          message
          href
        }
      }
      legal {
        copyright
      }

      socialIcons {
        telegram {
          icon
          href
        }
        youtube {
          icon
          href
        }
        instagram {
          icon
          href
        }
        twitter {
          icon
          href
        }
        facebook {
          icon
          href
        }
        linkedin {
          icon
          href
        }
        tiktok {
          icon
          href
        }
        discord {
          icon
          href
        }
      }
      appstore {
        src
        alt
        link
      }
      playstore {
        src
        alt
        link
      }
      copyright
    }
  }
`;

export default Footer;
